import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import moment from 'moment'
import type { Moment } from 'moment'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(customParseFormat)

export const REGISTRO_HORAS_TIMEZONE = 'America/Bogota'
export const BUSINESS_DATE_FORMAT = 'YYYY-MM-DD'

const BUSINESS_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/
const ISO_WITH_TIME_REGEX =
  /^\d{4}-\d{2}-\d{2}[T\s]\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:?\d{2})?$/
// Day-first or year-first calendar date with `/`, `-`, or `.` separators.
// e.g. "01/04/2026", "1-4-2026", "01.04.2026", "2026/04/01".
const CALENDAR_DATE_REGEX = /^(\d{1,4})[/.-](\d{1,2})[/.-](\d{1,4})$/
const EXCEL_EPOCH_UTC_MS = Date.UTC(1899, 11, 30)
const MS_PER_DAY = 86_400_000
const EXCEL_SERIAL_MIN = 365
const EXCEL_SERIAL_MAX = 90000

export type BusinessDate = string

export function isBusinessDate(value: unknown): value is BusinessDate {
  return typeof value === 'string' && BUSINESS_DATE_REGEX.test(value) && dayjs(value, BUSINESS_DATE_FORMAT, true).isValid()
}

function fromExcelSerial(value: number): BusinessDate {
  return dayjs.utc(EXCEL_EPOCH_UTC_MS + value * MS_PER_DAY).format(BUSINESS_DATE_FORMAT)
}

/**
 * Parses calendar dates pasted/typed with `/`, `-`, or `.` separators into a
 * canonical BusinessDate (YYYY-MM-DD). Slash/dash/dot dates are interpreted
 * day-first (es-CO locale, e.g. "01/04/2026" → April 1st), except when the
 * first segment is a 4-digit year ("2026/04/01" → year-first).
 *
 * Two-digit years are rejected to avoid guessing the century.
 * Returns '' when the value is not a recognizable calendar date.
 */
function parseFlexibleCalendarDate(value: string): BusinessDate {
  const match = CALENDAR_DATE_REGEX.exec(value)
  if (!match) return ''

  const [, first, second, third] = match
  let year: string
  let month: string
  let day: string

  if (first.length === 4) {
    // Year-first: YYYY/MM/DD
    year = first
    month = second
    day = third
  } else if (third.length === 4) {
    // Day-first: DD/MM/YYYY
    day = first
    month = second
    year = third
  } else {
    // Ambiguous (e.g. two-digit year) — refuse to guess.
    return ''
  }

  const iso = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
  return dayjs(iso, BUSINESS_DATE_FORMAT, true).isValid() ? iso : ''
}

export function toBusinessDate(value: unknown): BusinessDate {
  if (value == null || value === '') return ''

  if (typeof value === 'number' && Number.isFinite(value)) {
    if (value >= EXCEL_SERIAL_MIN && value <= EXCEL_SERIAL_MAX) {
      return fromExcelSerial(value)
    }
    return ''
  }

  if (value instanceof Date) {
    return Number.isFinite(value.getTime())
      ? dayjs(value).tz(REGISTRO_HORAS_TIMEZONE).format(BUSINESS_DATE_FORMAT)
      : ''
  }

  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed) return ''
    if (isBusinessDate(trimmed)) return trimmed
    if (ISO_WITH_TIME_REGEX.test(trimmed)) {
      const parsed = dayjs(trimmed)
      return parsed.isValid()
        ? parsed.tz(REGISTRO_HORAS_TIMEZONE).format(BUSINESS_DATE_FORMAT)
        : ''
    }
    return parseFlexibleCalendarDate(trimmed)
  }

  return ''
}

export function formatBusinessDate(value: unknown): string {
  return toBusinessDate(value)
}

export function businessDateToExcelSerial(value: unknown): number | null {
  const businessDate = toBusinessDate(value)
  if (!businessDate) return null
  const parsed = dayjs.utc(businessDate, BUSINESS_DATE_FORMAT, true)
  if (!parsed.isValid()) return null
  return (parsed.valueOf() - EXCEL_EPOCH_UTC_MS) / MS_PER_DAY
}

export function businessDateToUtcStartOfDay(value: unknown): string | null {
  const businessDate = toBusinessDate(value)
  if (!businessDate) return null
  return dayjs.tz(businessDate, BUSINESS_DATE_FORMAT, REGISTRO_HORAS_TIMEZONE).startOf('day').utc().toISOString()
}

export function businessDateToUtcEndOfDay(value: unknown): string | null {
  const businessDate = toBusinessDate(value)
  if (!businessDate) return null
  return dayjs.tz(businessDate, BUSINESS_DATE_FORMAT, REGISTRO_HORAS_TIMEZONE).endOf('day').utc().toISOString()
}

export function businessDateRangeToUtcDateTimeRange(dateRange: { from: string; to: string }): { gte: string; lte: string } {
  return {
    gte: businessDateToUtcStartOfDay(dateRange.from) ?? '',
    lte: businessDateToUtcEndOfDay(dateRange.to) ?? '',
  }
}

export function getTodayBusinessDate(): BusinessDate {
  return dayjs().tz(REGISTRO_HORAS_TIMEZONE).format(BUSINESS_DATE_FORMAT)
}

export function getRelativeBusinessDate(days: number): BusinessDate {
  return dayjs().tz(REGISTRO_HORAS_TIMEZONE).add(days, 'day').format(BUSINESS_DATE_FORMAT)
}

export function isFutureBusinessDate(value: unknown): boolean {
  const businessDate = toBusinessDate(value)
  if (!businessDate) return false
  return businessDate > getTodayBusinessDate()
}

export function businessDateToPickerMoment(value: unknown): Moment | null {
  const businessDate = toBusinessDate(value)
  if (!businessDate) return null
  const parsed = moment(businessDate, BUSINESS_DATE_FORMAT, true)
  return parsed.isValid() ? parsed : null
}

export function pickerMomentToBusinessDate(value: Moment | null | undefined): BusinessDate {
  if (!value || !value.isValid()) return ''
  return value.format(BUSINESS_DATE_FORMAT)
}
